import React, { useState, useEffect } from "react";
import { Navbar, Nav, Form, FormControl, Offcanvas } from "react-bootstrap";
import { NavLink } from "react-router-dom";
import { FaShoppingCart, FaUser, FaSearch } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import "./header.css";

const REACT_APP_API_URL = process.env.REACT_APP_API_URL;

const Header = () => {
  const [open, setOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [recommendations, setRecommendations] = useState([]);
  const [showRecommendations, setShowRecommendations] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  // Fetch all categories from the database
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${REACT_APP_API_URL}/products/categories`);

        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }

        const data = await res.json();
        console.log("Categories fetched successfully:", data);
        setCategories(data);
      } catch (err) {
        console.error("Failed to fetch categories:", err);
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // Fetch product recommendations as user types
  useEffect(() => {
    const fetchRecommendations = async () => {
      if (searchTerm.trim().length === 0) {
        setRecommendations([]);
        setShowRecommendations(false);
        return;
      }

      setSearchLoading(true);
      try {
        const res = await fetch(
          `${REACT_APP_API_URL}/products/search/recommendations?q=${encodeURIComponent(searchTerm)}`
        );
        
        if (res.ok) {
          const data = await res.json();
          setRecommendations(data);
          setShowRecommendations(true);
        }
      } catch (err) {
        console.error("Failed to fetch recommendations:", err);
        setRecommendations([]);
      } finally {
        setSearchLoading(false);
      }
    };

    // Debounce to avoid too many API calls
    const timeoutId = setTimeout(() => {
      fetchRecommendations();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      window.location.href = `/shop?search=${encodeURIComponent(searchTerm)}`;
    }
  };

  const handleRecommendationClick = (productId) => {
    setShowRecommendations(false);
    setSearchTerm("");
    window.location.href = `/product-details/${productId}`;
  };

  const getCategoryUrl = (category) => {
    return `/shop?category=${encodeURIComponent(category)}`;
  };

  // Helper function to safely format price
  const formatPrice = (price) => {
    if (!price && price !== 0) return "Price not available";
    return `₹${Number(price).toLocaleString()}`;
  };

  return (
    <div>
      <motion.div
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: "easeOut" }}
        className="header-main"
      >
        <Navbar expand="lg" className="header-navbar">
          {/* Logo */}
          <Navbar.Brand as={NavLink} to="/" className="logo-wrap">
            <img
              src="/images/logo.png"
              alt="Crescent Store"
              className="logo-img"
            />
          </Navbar.Brand>

          <Navbar.Toggle />

          <Navbar.Offcanvas placement="end">
            <Offcanvas.Header closeButton />
            <Offcanvas.Body>
              {/* Nav Links */}
              <Nav className="mx-auto nav-links align-items-center">
                <NavLink to="/aboutus" className="nav-link">
                  About Us
                </NavLink>

                {/* Shop Dropdown with Dynamic Categories */}
                <div
                  className="shop-dropdown"
                  onMouseEnter={() => setOpen(true)}
                  onMouseLeave={() => setOpen(false)}
                >
                  <NavLink to="/shop" className="nav-link">
                    Shop
                  </NavLink>

                  <AnimatePresence>
                    {open && !loading && categories.length > 0 && (
                      <motion.div
                        className="dropdown-menu-custom"
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 15 }}
                        transition={{ duration: 0.25, ease: "easeOut" }}
                      >
                        {categories.map((category) => (
                          <NavLink
                            key={category}
                            to={getCategoryUrl(category)}
                            className="dropdown-item"
                            onClick={() => setOpen(false)}
                          >
                            {category}
                          </NavLink>
                        ))}

                        <NavLink
                          to="/shop"
                          className="dropdown-item view-all"
                          onClick={() => setOpen(false)}
                        >
                          View All Products
                        </NavLink>
                      </motion.div>
                    )}
                    
                    {open && loading && (
                      <motion.div
                        className="dropdown-menu-custom loading"
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 15 }}
                      >
                        <div className="dropdown-item">
                          Loading categories...
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                <NavLink to="/offers" className="nav-link">
                  Offers
                </NavLink>
                <NavLink to="/brands" className="nav-link">
                  Brands
                </NavLink>
                <NavLink to="/contactus" className="nav-link">
                  Contact
                </NavLink>
              </Nav>

              {/* Search with Product Recommendations */}
              <div className="search-container">
                <Form onSubmit={handleSearchSubmit} className="search-box">
                  <div className="search-input-wrapper">
                    <FormControl
                      type="search"
                      name="search"
                      className="border"
                      placeholder="Search products..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onFocus={() => searchTerm.trim() && setShowRecommendations(true)}
                      onBlur={() => setTimeout(() => setShowRecommendations(false), 200)}
                    />
                    <button type="submit" className="search-icon-btn">
                      <FaSearch />
                    </button>
                  </div>
                </Form>

                {/* Product Recommendations Dropdown */}
                {showRecommendations && (
                  <div className="recommendations-dropdown">
                    {searchLoading ? (
                      <div className="recommendation-loading">
                        Loading products...
                      </div>
                    ) : recommendations.length > 0 ? (
                      <>
                        <div className="recommendations-header">
                          <span>Products</span>
                          <NavLink 
                            to={`/shop?search=${encodeURIComponent(searchTerm)}`}
                            className="view-all-link"
                            onClick={() => setShowRecommendations(false)}
                          >
                            View All ({recommendations.length})
                          </NavLink>
                        </div>
                        {recommendations.map((product) => (
                          <div
                            key={product._id}
                            className="recommendation-item"
                            onClick={() => handleRecommendationClick(product._id)}
                          >
                            {/* <img 
                              src={product.images?.[0] || "/images/placeholder.jpg"} 
                              alt={product.ProductName || product.name}
                              className="recommendation-img"
                            /> */}
                            <div className="recommendation-info">
                              <div className="recommendation-name">
                                {product.ProductName || product.name || "Product Name"}
                              </div>
                              {product.category && (
                                <div className="recommendation-category">
                                  {product.category}
                                </div>
                              )}
                              <div className="recommendation-price">
                                {/* FIXED: Safely format price */}
                                {formatPrice(product.price || product.Price)}
                                
                                {/* Safely show original price */}
                                {(product.originalPrice || product.OriginalPrice) && (
                                  <span className="original-price">
                                    {formatPrice(product.originalPrice || product.OriginalPrice)}
                                  </span>
                                )}
                                
                                {/* Safely show discount */}
                                {(product.discount || product.Discount) && (
                                  <span className="discount-badge">
                                    {(product.discount || product.Discount)}% OFF
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </>
                    ) : searchTerm.trim().length > 0 && (
                      <div className="no-results">
                        <p>No products found for "{searchTerm}"</p>
                        <NavLink 
                          to={`/shop?search=${encodeURIComponent(searchTerm)}`}
                          onClick={() => setShowRecommendations(false)}
                        >
                          Browse all products
                        </NavLink>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Icons */}
              <div className="header-icons me-3">
                <motion.div whileHover={{ scale: 1.2 }}>
                  <NavLink to="/cart" className="icon-link">
                    <FaShoppingCart />
                  </NavLink>
                </motion.div>

                <motion.div whileHover={{ scale: 1.2 }}>
                  <NavLink to="/login" className="icon-link">
                    <FaUser />
                  </NavLink>
                </motion.div>
              </div>
            </Offcanvas.Body>
          </Navbar.Offcanvas>
        </Navbar>
      </motion.div>
    </div>
  );
};

export default Header;